/**
 * 实时消息流入，批量获取会话信息
 *
 * 基于 BatchConcurrentQueue 重构，统一代码风格
 *
 * 优化点：
 * 1. 支持 pending 阶段复用：请求进行中时，新进来的相同 conversationId 会加入到 pending 回调队列
 * 2. 并发控制：限制同时进行的请求数量，避免接口压力过大
 * 3. 批量合并：延迟 3 秒后合并请求，减少请求次数
 */

import { logger } from 'services/logger';
import { getTalkInfoListWithPhone, syncMessage } from '../api/conversation.convList';
import { needSyncTalkChannel } from 'hooks/business/conversation';
import { isInSyncTalkRange } from 'hooks/business/useSyncTalkIds';
import getNeedSyncIds from 'hooks/business/conversationNeedSync';
import { BatchConcurrentQueue } from 'utils/BatchConcurrentQueue';

// 配置参数
const DELAY_TIME = 3000; // 批量请求延迟时间
const MAX_REQUEST_COUNT = 20; // 单次请求最大 ID 数量
const MAX_CONCURRENT_REQUESTS = 3; // 最大并发请求数

// 会话批量获取队列实例
const talkQueue = new BatchConcurrentQueue<string, Conversation.Conversation>({
  concurrency: MAX_CONCURRENT_REQUESTS,
  batchSize: MAX_REQUEST_COUNT,
  debounceMs: DELAY_TIME,
  executor: async (conversationIds, signal) => {
    logger.info(
      `[batchGetTalk] 时间: ${new Date().toISOString()}, 发起批量请求: ${conversationIds.join(
        ',',
      )}`,
    );

    const res = await getTalkInfoListWithPhone(conversationIds, signal);
    const resultMap = new Map<string, Conversation.Conversation>();

    if (res?.aggTalks?.length > 0) {
      const syncTalkIds: string[] = [];
      const d = Date.now();

      res.aggTalks.forEach(({ talk }) => {
        resultMap.set(talk.conversationId, talk);

        // 检查是否需要同步消息
        if (needSyncTalkChannel.includes(talk.channel) && isInSyncTalkRange(talk, d)) {
          syncTalkIds.push(talk.talkId);
        }
      });

      // 处理同步消息
      if (syncTalkIds.length > 0) {
        const talkIds = getNeedSyncIds(syncTalkIds, 120);
        if (talkIds.length > 0) {
          syncMessage({ talkIds });
        }
      }
    }

    return resultMap;
  },
  onBatchError: (_keys, error) => {
    logger.error(
      `[batchGetTalk] 时间: ${new Date().toISOString()}, 批量请求错误: ${
        error?.message || 'unknown error'
      }`,
    );
  },
});

/**
 * 批量获取会话信息
 * @param conversationId 会话 ID
 * @returns Promise<Conversation.Conversation>
 */
export function batchGetTalk(conversationId: string): Promise<Conversation.Conversation> {
  logger.info(
    `[batchGetTalk] 时间: ${new Date().toISOString()}, 加入队列: ${conversationId}, ` +
      `当前等待: ${talkQueue.getWaitingSize()}, 当前并发: ${talkQueue.getCurrentConcurrent()}`,
  );

  return talkQueue.enqueue(conversationId);
}

/**
 * 取消获取指定会话信息（仅限等待队列中的）
 * @param conversationId 会话 ID
 */
export function cancelBatchGetTalk(conversationId: string): void {
  talkQueue.cancel(conversationId);
}

/**
 * 检查会话是否正在获取中
 * @param conversationId 会话 ID
 */
export function isTalkPending(conversationId: string): boolean {
  return talkQueue.isPending(conversationId);
}

/**
 * 检查会话是否在等待队列中
 * @param conversationId 会话 ID
 */
export function isTalkWaiting(conversationId: string): boolean {
  return talkQueue.isWaiting(conversationId);
}
